import React, { useState, useEffect, useMemo } from "react";
import { CSVLink } from "react-csv";
import API from "../../../services/api";

/**
 * Campaign Report 1 – pivot table of daily brand/campaign stats per sales person.
 *
 * Props (managed by parent FilterBar via index.js):
 *   month       – "YYYY-MM" string
 *   brandType   – { value, label } | null
 *   salesPerson – { value, label } | null
 */
const CampaignReport1 = ({ month, brandType, salesPerson }) => {
    const [data, setData] = useState([]);
    const [totals, setTotals] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const getData = async () => {
            setLoading(true);
            try {
                const result = await API({
                    url: "/accountManagement/getCampaignsReport1",
                    method: "POST",
                    data: {
                        month,
                        user_type: brandType?.value ?? null,
                        sales_person_id: salesPerson?.value ?? null,
                    },
                });
                if (result?.code === 200) {
                    setData(result.data?.data || []);
                    setTotals(result.data?.total || []);
                }
            } finally {
                setLoading(false);
            }
        };
        getData();
    }, [month, brandType, salesPerson]);

    // Days in the selected month
    const days = useMemo(() => {
        const [y, m] = month.split("-").map(Number);
        const count = new Date(y, m, 0).getDate();
        return Array.from({ length: count }, (_, i) => i + 1);
    }, [month]);

    const pivotData = useMemo(() => {
        const map = {};
        data.forEach((sp) => {
            const name = sp.name || "—";
            if (!map[name]) map[name] = {};
            sp.monthly_stats?.forEach((stat) => {
                const day = new Date(stat.date * 1000).getDate();
                if (!map[name][day]) map[name][day] = { brand_count: 0, campaign_count: 0, known: 0, unknown: 0, missed: 0 };
                map[name][day].brand_count += +stat.brand_count || 0;
                map[name][day].campaign_count += +stat.campaign_count || 0;
                map[name][day].known += +stat.known || 0;
                map[name][day].unknown += +stat.unknown || 0;
                map[name][day].missed += +stat.missed || 0;
            });
        });
        return Object.entries(map).map(([name, dayData]) => ({ name, dayData }));
    }, [data]);

    const totalCols = 1 + days.length * 5 + 5;

    const rowTotals = useMemo(() => {
        return pivotData.map(({ name }) => {
            const total = totals.find((t) => t.name === name);
            return {
                totalBrands: +total?.brand_count || 0,
                totalCampaigns: +total?.campaign_count || 0,
                totalKnown: +total?.known || 0,
                totalUnknown: +total?.unknown || 0,
                totalMissed: +total?.missed || 0,
            };
        });
    }, [pivotData, totals]);

    const columnTotals = useMemo(() => {
        const totals = {};
        days.forEach((day) => {
            totals[day] = { brand_count: 0, campaign_count: 0, known: 0, unknown: 0, missed: 0 };
        });
        pivotData.forEach(({ dayData }) => {
            days.forEach((day) => {
                const m = dayData[day];
                if (m) {
                    totals[day].brand_count += +m.brand_count || 0;
                    totals[day].campaign_count += +m.campaign_count || 0;
                    totals[day].known += +m.known || 0;
                    totals[day].unknown += +m.unknown || 0;
                    totals[day].missed += +m.missed || 0;
                }
            });
        });
        return totals;
    }, [pivotData, days]);

    const grandTotals = useMemo(() => {
        let totalBrands = 0,
            totalCampaigns = 0,
            totalKnown = 0,
            totalUnknown = 0,
            totalMissed = 0;
        rowTotals.forEach(({ totalBrands: tb, totalCampaigns: tc, totalKnown: tk, totalUnknown: tu, totalMissed: tm }) => {
            totalBrands += tb;
            totalCampaigns += tc;
            totalKnown += tk;
            totalUnknown += tu;
            totalMissed += tm;
        });
        return { totalBrands, totalCampaigns, totalKnown, totalUnknown, totalMissed };
    }, [rowTotals]);

    // CSV data
    const csvData = useMemo(() => {
        const rows = [];
        const h1 = ["Sales Person"];
        days.forEach((d) => h1.push(String(d).padStart(2, "0"), "", "", "", ""));
        h1.push("Total", "", "", "", "");
        rows.push(h1);

        const h2 = [""];
        days.forEach(() => h2.push("Brands", "Campaigns", "Known", "Unknown", "Missed"));
        h2.push("Brands", "Campaigns", "Known", "Unknown", "Missed");
        rows.push(h2);

        pivotData.forEach(({ name, dayData }, idx) => {
            const rt = rowTotals[idx];
            const row = [name];
            days.forEach((d) => {
                const m = dayData[d] || { brand_count: 0, campaign_count: 0, known: 0, unknown: 0, missed: 0 };
                row.push(m.brand_count, m.campaign_count, m.known, m.unknown, m.missed);
            });
            row.push(rt.totalBrands, rt.totalCampaigns, rt.totalKnown, rt.totalUnknown, rt.totalMissed);
            rows.push(row);
        });

        const footer = ["Total"];
        days.forEach((d) => {
            const ct = columnTotals[d];
            footer.push(ct.brand_count, ct.campaign_count, ct.known, ct.unknown, ct.missed);
        });
        footer.push(grandTotals.totalBrands, grandTotals.totalCampaigns, grandTotals.totalKnown, grandTotals.totalUnknown, grandTotals.totalMissed);
        rows.push(footer);

        return rows;
    }, [pivotData, days, rowTotals, columnTotals, grandTotals]);

    return (
        <div className="p-4">
            {/* Table toolbar */}
            <div className="flex justify-end mb-3">
                <CSVLink data={csvData} filename={`campaign_report_1_${month}.csv`} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-semibold hover:bg-emerald-700 transition-colors">
                    Download CSV
                </CSVLink>
            </div>

            {/* Pivot table */}
            <div style={{ maxWidth: "100%", maxHeight: "70vh", overflow: "auto", position: "relative" }}>
                <table
                    style={{
                        minWidth: `${totalCols * 60}px`,
                        borderCollapse: "collapse",
                        borderSpacing: 0,
                        fontSize: "12px",
                    }}
                >
                    <thead style={{ position: "sticky", top: 0, zIndex: 40 }}>
                        {/* Row 1 – day numbers */}
                        <tr>
                            <th className="px-4 py-2.5 font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border border-gray-200 text-left bg-white" style={{ position: "sticky", left: 0, zIndex: 50, minWidth: "180px" }}>
                                Sales Person
                            </th>
                            {days.map((day) => (
                                <th key={day} colSpan={5} className="px-2 py-2.5 font-semibold text-gray-700 uppercase tracking-wider whitespace-nowrap border border-gray-200 text-center bg-white">
                                    {String(day).padStart(2, "0")}
                                </th>
                            ))}
                            <th colSpan={5} className="px-2 py-2.5 font-bold text-gray-900 uppercase tracking-wider whitespace-nowrap border border-gray-200 text-center bg-gray-100" style={{ position: "sticky", right: 0, zIndex: 50 }}>
                                Total
                            </th>
                        </tr>

                        {/* Row 2 – sub-column labels */}
                        <tr>
                            <th className="px-4 py-1.5 font-semibold text-gray-500 text-xs whitespace-nowrap border border-gray-200 bg-white" style={{ position: "sticky", left: 0, zIndex: 50, minWidth: "180px" }} />
                            {days.map((day) => (
                                <React.Fragment key={day}>
                                    <th className="px-1 py-1.5 font-semibold text-gray-600 text-xs whitespace-nowrap border border-gray-200 text-center bg-white">Brands</th>
                                    <th className="px-1 py-1.5 font-semibold text-blue-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-white">Campaigns</th>
                                    <th className="px-1 py-1.5 font-semibold text-emerald-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-white">Known</th>
                                    <th className="px-1 py-1.5 font-semibold text-orange-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-white">Unknown</th>
                                    <th className="px-1 py-1.5 font-semibold text-rose-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-white">Missed</th>
                                </React.Fragment>
                            ))}
                            <th className="px-1 py-1.5 font-semibold text-gray-700 text-xs whitespace-nowrap border border-gray-200 text-center bg-gray-100" style={{ position: "sticky", right: "240px", zIndex: 50, width: "60px" }}>
                                Brands
                            </th>
                            <th className="px-1 py-1.5 font-semibold text-blue-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-gray-100" style={{ position: "sticky", right: "180px", zIndex: 50, width: "60px" }}>
                                Campaigns
                            </th>
                            <th className="px-1 py-1.5 font-semibold text-emerald-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-gray-100" style={{ position: "sticky", right: "120px", zIndex: 50, width: "60px" }}>
                                Known
                            </th>
                            <th className="px-1 py-1.5 font-semibold text-orange-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-gray-100" style={{ position: "sticky", right: "60px", zIndex: 50, width: "60px" }}>
                                Unknown
                            </th>
                            <th className="px-1 py-1.5 font-semibold text-rose-700/70 text-xs whitespace-nowrap border border-gray-200 text-center bg-gray-100" style={{ position: "sticky", right: 0, zIndex: 50, width: "60px" }}>
                                Missed
                            </th>
                        </tr>
                    </thead>

                    {loading ? (
                        <tbody>
                            <tr>
                                <td colSpan={totalCols} className="py-8 text-center text-sm text-gray-400">
                                    Loading…
                                </td>
                            </tr>
                        </tbody>
                    ) : pivotData.length > 0 ? (
                        <>
                            <tbody>
                                {pivotData.map(({ name, dayData }, idx) => {
                                    const rt = rowTotals[idx];
                                    return (
                                        <tr key={idx} className={`border-t border-gray-100 hover:bg-blue-50 transition-colors bg-gray-50`}>
                                            <td className="px-4 py-1.5 whitespace-nowrap font-medium text-gray-800 border border-gray-200 text-left bg-white" style={{ position: "sticky", left: 0, zIndex: 30, minWidth: "180px" }}>
                                                {name}
                                            </td>
                                            {days.map((day) => {
                                                const m = dayData[day];
                                                const v = m || { brand_count: 0, campaign_count: 0, known: 0, unknown: 0, missed: 0 };
                                                const dim = !m ? "opacity-30" : "";
                                                return (
                                                    <React.Fragment key={day}>
                                                        <td className={`px-1 py-1.5 text-center border border-gray-200 font-medium text-gray-700 ${dim}`} title={`Day ${day} – Brands: ${v.brand_count}`}>
                                                            {v.brand_count}
                                                        </td>
                                                        <td className={`px-1 py-1.5 text-center border border-gray-200 font-medium text-blue-700/70 ${dim}`} title={`Day ${day} – Campaigns: ${v.campaign_count}`}>
                                                            {v.campaign_count}
                                                        </td>
                                                        <td className={`px-1 py-1.5 text-center border border-gray-200 font-medium text-emerald-700/70 ${dim}`} title={`Day ${day} – Known: ${v.known}`}>
                                                            {v.known}
                                                        </td>
                                                        <td className={`px-1 py-1.5 text-center border border-gray-200 font-medium text-orange-700/70 ${dim}`} title={`Day ${day} – Unknown: ${v.unknown}`}>
                                                            {v.unknown}
                                                        </td>
                                                        <td className={`px-1 py-1.5 text-center border border-gray-200 font-medium text-rose-700/70 ${dim}`} title={`Day ${day} – Missed: ${v.missed}`}>
                                                            {v.missed}
                                                        </td>
                                                    </React.Fragment>
                                                );
                                            })}
                                            <td className="px-1 py-1.5 text-center border border-gray-200 font-medium text-gray-700 bg-gray-100" style={{ position: "sticky", right: "240px", zIndex: 25, width: "60px" }}>
                                                {rt.totalBrands}
                                            </td>
                                            <td className="px-1 py-1.5 text-center border border-gray-200 font-medium text-blue-700/70 bg-gray-100" style={{ position: "sticky", right: "180px", zIndex: 25, width: "60px" }}>
                                                {rt.totalCampaigns}
                                            </td>
                                            <td className="px-1 py-1.5 text-center border border-gray-200 font-medium text-emerald-700/70 bg-gray-100" style={{ position: "sticky", right: "120px", zIndex: 25, width: "60px" }}>
                                                {rt.totalKnown}
                                            </td>
                                            <td className="px-1 py-1.5 text-center border border-gray-200 font-medium text-orange-700/70 bg-gray-100" style={{ position: "sticky", right: "60px", zIndex: 25, width: "60px" }}>
                                                {rt.totalUnknown}
                                            </td>
                                            <td className="px-1 py-1.5 text-center border border-gray-200 font-medium text-rose-700/70 bg-gray-100" style={{ position: "sticky", right: 0, zIndex: 25, width: "60px" }}>
                                                {rt.totalMissed}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>

                            <tfoot style={{ position: "sticky", bottom: 0, zIndex: 40 }}>
                                <tr>
                                    <td className="px-4 py-1.5 border border-gray-300 text-center font-bold text-gray-900 bg-gray-200" style={{ position: "sticky", left: 0, zIndex: 50, minWidth: "180px" }}>
                                        Total
                                    </td>
                                    {days.map((day) => {
                                        const ct = columnTotals[day];
                                        return (
                                            <React.Fragment key={day}>
                                                <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-gray-700 bg-gray-200">{ct.brand_count}</td>
                                                <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-blue-700/70 bg-gray-200">{ct.campaign_count}</td>
                                                <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-emerald-700/70 bg-gray-200">{ct.known}</td>
                                                <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-orange-700/70 bg-gray-200">{ct.unknown}</td>
                                                <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-rose-700/70 bg-gray-200">{ct.missed}</td>
                                            </React.Fragment>
                                        );
                                    })}
                                    <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-gray-700 bg-gray-300" style={{ position: "sticky", right: "240px", zIndex: 45, width: "60px" }}>
                                        {grandTotals.totalBrands}
                                    </td>
                                    <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-blue-700/70 bg-gray-300" style={{ position: "sticky", right: "180px", zIndex: 45, width: "60px" }}>
                                        {grandTotals.totalCampaigns}
                                    </td>
                                    <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-emerald-700/70 bg-gray-300" style={{ position: "sticky", right: "120px", zIndex: 45, width: "60px" }}>
                                        {grandTotals.totalKnown}
                                    </td>
                                    <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-orange-700/70 bg-gray-300" style={{ position: "sticky", right: "60px", zIndex: 45, width: "60px" }}>
                                        {grandTotals.totalUnknown}
                                    </td>
                                    <td className="px-1 py-1.5 text-center border border-gray-300 font-medium text-rose-700/70 bg-gray-300" style={{ position: "sticky", right: 0, zIndex: 45, width: "60px" }}>
                                        {grandTotals.totalMissed}
                                    </td>
                                </tr>
                            </tfoot>
                        </>
                    ) : (
                        <tbody>
                            <tr>
                                <td colSpan={totalCols} className="py-12 text-center text-sm text-gray-400">
                                    No data found
                                </td>
                            </tr>
                        </tbody>
                    )}
                </table>
            </div>
        </div>
    );
};

export default CampaignReport1;
